import { useCallback, useEffect } from 'react'

import { selectProcessDocumentCurrentUser } from '../../../store/processDocument/documentUsers'
import { useGetAccountQuery } from '../../../store/accountUserSettings/accountUserSettings'
import { selectProcessDocumentFileInfo } from '../../../store/processDocument/documentInfo'
import { useLazyGetContactSchemaQuery } from '../../../store/integrations/integrations'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'
import {
  clearDocumentProspectingDetailsForm,
  resetDocumentProspecting,
  selectDocumentProspectingDetailsFormProps,
  selectDocumentProspectingDetailsFormPropsLoading,
  selectDocumentProspectingServiceId,
  setDocumentProspectingDetailsFormProps,
  setDocumentProspectingDetailsFormPropsLoading,
  setDocumentProspectingServiceId,
} from '../../../store/processDocument/documentProspecting'

import {
  DOCUMENT_CONTACT_UPDATE_SUPPORTED_INTEGRATIONS,
  INTEGRATIONS_WITH_DYNAMIC_SCHEMA,
  INTEGRATIONS_WITH_SEGMENTED_CONTACT_CHANGE,
  IntegrationServiceId,
} from '../../../common/integrations'

import { AccountIntegrationConfigMappingType } from '../../../models/Account'

import { useProspectingDetailsForm } from '../DocumentProspecting/hooks'

import { useInitializeDocumentInfo, useInitializeDocumentSummary, useInitializeDocumentTypes, useInitializeDocumentUsers } from './hooks'
import { getAccountIntegrationConfigMappings } from './utils'

export const DocumentProspectingInitialization = () => {
  useInitializeDocumentInfo()
  useInitializeDocumentTypes()
  useInitializeDocumentUsers()
  useInitializeDocumentProspecting()
  useInitializeDocumentSummary()

  return null
}

const useInitializeDocumentProspecting = () => {
  const dispatch = useAppDispatch()
  const currentUser = useAppSelector(selectProcessDocumentCurrentUser)

  useInitializeDocumentProspectingDetailsForm()

  useEffect(() => {
    if (!currentUser) {
      return
    }

    const integrations =
      currentUser.integrations?.filter(({ serviceId }) => DOCUMENT_CONTACT_UPDATE_SUPPORTED_INTEGRATIONS.includes(serviceId as IntegrationServiceId)) ?? []

    if (integrations.length === 1) {
      const { serviceId } = integrations[0]
      dispatch(setDocumentProspectingServiceId(serviceId as IntegrationServiceId))
    }
  }, [currentUser, dispatch])

  useEffect(() => {
    return () => {
      dispatch(resetDocumentProspecting())
    }
  }, [dispatch])
}

const useInitializeDocumentProspectingDetailsForm = () => {
  const dispatch = useAppDispatch()

  const accountId = useAppSelector(selectAccountId)
  const fileInfo = useAppSelector(selectProcessDocumentFileInfo)
  const serviceId = useAppSelector(selectDocumentProspectingServiceId)

  const detailsFormProps = useAppSelector(selectDocumentProspectingDetailsFormProps)
  const detailsFormPropsLoading = useAppSelector(selectDocumentProspectingDetailsFormPropsLoading)

  const { data: accountResponse } = useGetAccountQuery({ accountId })
  const [triggerGetIntegrationContactSchema] = useLazyGetContactSchemaQuery()

  const { generateFormValues } = useProspectingDetailsForm()

  const initializeDetailsFormForIntegrationWithStaticSchema = useCallback(
    async (serviceId: IntegrationServiceId, contactObjectName: string) => {
      dispatch(setDocumentProspectingDetailsFormPropsLoading(true))
      dispatch(clearDocumentProspectingDetailsForm())
      try {
        const { data: integrationContactSchemaResponse } = await triggerGetIntegrationContactSchema({
          accountId,
          connector: serviceId,
          context: { objectName: contactObjectName },
        })
        if (!integrationContactSchemaResponse) {
          return
        }
        const { data: schema } = integrationContactSchemaResponse
        const previousValues = {}
        const propsType = INTEGRATIONS_WITH_SEGMENTED_CONTACT_CHANGE.includes(serviceId) ? 'segmented' : 'default'
        dispatch(setDocumentProspectingDetailsFormProps({ type: propsType, schema, previousValues }))
      } catch (e) {
        console.error(e)
      } finally {
        dispatch(setDocumentProspectingDetailsFormPropsLoading(false))
      }
    },
    [accountId, dispatch, triggerGetIntegrationContactSchema],
  )

  useEffect(() => {
    if (!serviceId || !accountResponse?.data || !fileInfo) {
      return
    }

    if (INTEGRATIONS_WITH_DYNAMIC_SCHEMA.includes(serviceId)) {
      return
    }

    const { data: account } = accountResponse
    const accountIntegrationConfigMappings = getAccountIntegrationConfigMappings(account, serviceId)
    const contactObjectName = accountIntegrationConfigMappings[AccountIntegrationConfigMappingType.CONTACT]

    initializeDetailsFormForIntegrationWithStaticSchema(serviceId, contactObjectName)
  }, [accountResponse, fileInfo, initializeDetailsFormForIntegrationWithStaticSchema, serviceId])

  useEffect(() => {
    if (!serviceId || !fileInfo || !detailsFormProps || detailsFormPropsLoading) {
      return
    }

    const { fileId } = fileInfo

    generateFormValues(serviceId, fileId, detailsFormProps)
  }, [detailsFormProps, detailsFormPropsLoading, fileInfo, generateFormValues, serviceId])
}
